// Paste a FEN or a PGN and play on from it, against the bot or on one device.
import type { ChessMenuContext, LocalOpponent, MenuScreen } from '../types.js';
import { createScreenShell, createMenuHeader, createCrestButton, createPasteButton, el } from '@bn-games/ui-shell';
import { t } from '../../i18n/I18n.js';
import { importGame, type ImportResult } from '../../game/importGame.js';
import { backRow } from './backButton.js';

const INPUT_ID = 'chess-import-input';
const STATUS_ID = 'chess-import-status';

function statusText(result: ImportResult): string {
  if (!result.ok) return t(`importPosition.errors.${result.error}`);
  const { turn, position } = result.state;
  return t(turn === 'w' ? 'importPosition.readyWhite' : 'importPosition.readyBlack', { move: position.fullmoveNumber });
}

export function createImportPositionScreen(): MenuScreen {
  let frame: HTMLElement | null = null;

  return {
    mount(root, ctx: ChessMenuContext) {
      const shell = createScreenShell();
      frame = shell.frame;

      const input = el('textarea', {
        class: 'chess-import__input',
        attrs: {
          id: INPUT_ID, rows: '7', spellcheck: 'false', autocapitalize: 'off', autocomplete: 'off',
          'aria-describedby': STATUS_ID,
        },
      }) as HTMLTextAreaElement;
      const status = el('div', { class: 'chess-import__status', attrs: { id: STATUS_ID, role: 'status' } });

      // Nothing pasted yet is not an error to announce; a refused paste is.
      const show = (result: ImportResult, quietIfEmpty: boolean): void => {
        const quiet = quietIfEmpty && !result.ok && result.error === 'empty';
        status.textContent = quiet ? '' : statusText(result);
        status.classList.toggle('chess-import__status--error', !result.ok && !quiet);
      };
      const check = (): void => show(importGame(input.value), true);
      const play = (opponent: LocalOpponent): void => {
        const result = importGame(input.value);
        show(result, false);
        if (result.ok) ctx.startLocalGame(opponent, result.state);
        else input.focus();
      };
      input.addEventListener('input', check);

      const paste = createPasteButton({
        label: t('importPosition.paste'),
        // The default strips all whitespace (room codes); a game is pasted as written.
        sanitize: (text) => text,
        onPaste: (text) => {
          input.value = text;
          check();
        },
      });
      const form = el('div', { class: 'about-center' }, [
        el('div', { class: 'button-stack' }, [
          el('label', { class: 'about-paragraph', attrs: { for: INPUT_ID }, text: t('importPosition.hint') }),
          input,
          el('div', { class: 'button-row' }, [paste]),
          status,
          createCrestButton({ label: t('importPosition.playBot'), primary: true, onClick: () => play('bot') }),
          createCrestButton({ label: t('importPosition.playHuman'), onClick: () => play('human') }),
        ]),
      ]);

      shell.body.append(createMenuHeader({ title: t('importPosition.title') }), form, backRow(ctx, 'play'));
      root.appendChild(frame);
      input.focus();
    },
    unmount() {
      frame?.remove();
      frame = null;
    },
  };
}
